import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, RefreshCw, Copy, Check, FileSignature, ShieldCheck, Calendar, ClipboardList, MessageSquare } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { useAuth, authAxios } from '../context/AuthContext';

const statusColors = {
  pending: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/40',
  confirmed: 'bg-blue-500/20 text-blue-300 border-blue-500/40',
  completed: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40',
  cancelled: 'bg-red-500/20 text-red-300 border-red-500/40',
};

function BookingRow({ booking, onRefresh }) {
  const [estimate, setEstimate] = useState(booking.estimate_amount ?? '');
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  const saveEstimate = async () => {
    const val = parseFloat(estimate);
    if (!val || val <= 0) {
      toast.error('Enter a valid estimate amount');
      return;
    }
    setSaving(true);
    try {
      await authAxios().put(`/admin/bookings/${booking.id}/estimate`, { estimate_amount: val });
      toast.success('Estimate saved & deposit calculated');
      onRefresh();
    } catch {
      toast.error('Failed to save estimate');
    } finally {
      setSaving(false);
    }
  };

  const updateStatus = async (status) => {
    try {
      await authAxios().put(`/admin/bookings/${booking.id}/status`, { status });
      toast.success(`Status set to ${status}`);
      onRefresh();
    } catch {
      toast.error('Failed to update status');
    }
  };

  const toggleDepositPaid = async () => {
    try {
      await authAxios().put(`/admin/bookings/${booking.id}/deposit-paid`, { deposit_paid: !booking.deposit_paid });
      toast.success(booking.deposit_paid ? 'Marked deposit unpaid' : 'Marked deposit paid');
      onRefresh();
    } catch {
      toast.error('Failed to update deposit');
    }
  };

  const copyPayLink = () => {
    const link = `${window.location.origin}/pay-deposit/${booking.id}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    toast.success('Pay-deposit link copied');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="glass rounded-xl p-5 space-y-4" data-testid={`booking-row-${booking.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-heading text-lg font-bold text-white">{booking.customer_name}</h3>
            <Badge className="bg-[#00F0FF]/15 text-[#00F0FF] border-[#00F0FF]/40 capitalize">{booking.service_type}</Badge>
            {booking.request_contract && (
              <Badge className="bg-purple-500/20 text-purple-300 border-purple-500/40 flex items-center gap-1">
                <FileSignature className="w-3 h-3" /> Contract Requested
              </Badge>
            )}
          </div>
          <p className="text-gray-400 text-sm mt-1">{booking.email} · {booking.phone}</p>
          <p className="text-gray-500 text-sm flex items-center gap-1 mt-1">
            <Calendar className="w-3.5 h-3.5" /> {booking.preferred_date} at {booking.preferred_time}
          </p>
          <p className="text-gray-500 text-sm mt-1">{booking.address}</p>
          {booking.description && <p className="text-gray-400 text-sm mt-2 italic">"{booking.description}"</p>}
        </div>
        <div className="flex flex-col items-end gap-2">
          <Select value={booking.status} onValueChange={updateStatus}>
            <SelectTrigger data-testid={`status-select-${booking.id}`} className="w-40 bg-[#0A0F14] border-[#1E293B] text-white rounded-none">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#0A0F14] border-[#1E293B]">
              {['pending', 'confirmed', 'completed', 'cancelled'].map((s) => (
                <SelectItem key={s} value={s} className="text-white capitalize hover:bg-[#1E293B] focus:bg-[#1E293B]">{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Badge className={`capitalize border ${statusColors[booking.status] || ''}`}>{booking.status}</Badge>
        </div>
      </div>

      <div className="border-t border-[#1E293B] pt-4 grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
        <div className="space-y-1">
          <label className="text-xs text-gray-400 uppercase tracking-wide">Estimate ($)</label>
          <div className="flex gap-2">
            <Input
              type="number"
              min="0"
              step="0.01"
              data-testid={`estimate-input-${booking.id}`}
              value={estimate}
              onChange={(e) => setEstimate(e.target.value)}
              placeholder="0.00"
              className="bg-[#0A0F14] border-[#1E293B] text-white rounded-none focus:border-[#00F0FF]"
            />
            <Button onClick={saveEstimate} disabled={saving} data-testid={`save-estimate-${booking.id}`} className="btn-primary rounded-none px-4">
              {saving ? '...' : 'Set'}
            </Button>
          </div>
        </div>
        <div className="space-y-1">
          <label className="text-xs text-gray-400 uppercase tracking-wide">Deposit Due</label>
          <p className="text-white text-lg font-bold">
            {booking.deposit_amount != null ? `$${booking.deposit_amount.toFixed(2)}` : '—'}
          </p>
        </div>
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="outline"
            onClick={toggleDepositPaid}
            data-testid={`deposit-toggle-${booking.id}`}
            className={`rounded-none border ${booking.deposit_paid ? 'border-emerald-500/50 text-emerald-300' : 'border-[#1E293B] text-gray-300'}`}
          >
            {booking.deposit_paid ? <><Check className="w-4 h-4 mr-1" /> Deposit Paid</> : 'Mark Paid'}
          </Button>
          <Button
            variant="outline"
            onClick={copyPayLink}
            data-testid={`copy-link-${booking.id}`}
            disabled={booking.deposit_amount == null}
            className="rounded-none border-[#1E293B] text-[#00F0FF]"
          >
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    try {
      const [b, c] = await Promise.all([
        authAxios().get('/admin/bookings'),
        authAxios().get('/admin/contacts'),
      ]);
      setBookings(b.data);
      setContacts(c.data);
    } catch {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  return (
    <main className="min-h-screen bg-[#020408] px-6 md:px-12 lg:px-16 py-10" data-testid="admin-dashboard">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-[#00F0FF]/20 border border-[#00F0FF]/50 flex items-center justify-center cyan-glow">
              <ShieldCheck className="w-6 h-6 text-[#00F0FF]" />
            </div>
            <div>
              <h1 className="font-heading text-2xl font-bold">Admin Dashboard</h1>
              <p className="text-gray-400 text-sm">{admin?.email}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={loadData} data-testid="refresh-button" className="rounded-none border-[#1E293B] text-gray-300">
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
            <Button variant="outline" onClick={handleLogout} data-testid="logout-button" className="rounded-none border-[#1E293B] text-gray-300">
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </Button>
          </div>
        </div>

        <Tabs defaultValue="bookings">
          <TabsList className="bg-[#0A0F14] border border-[#1E293B] rounded-none mb-6">
            <TabsTrigger value="bookings" data-testid="tab-bookings" className="rounded-none data-[state=active]:bg-[#00F0FF]/15 data-[state=active]:text-[#00F0FF]">
              <ClipboardList className="w-4 h-4 mr-2" /> Bookings ({bookings.length})
            </TabsTrigger>
            <TabsTrigger value="contacts" data-testid="tab-contacts" className="rounded-none data-[state=active]:bg-[#00F0FF]/15 data-[state=active]:text-[#00F0FF]">
              <MessageSquare className="w-4 h-4 mr-2" /> Contacts ({contacts.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="bookings" className="space-y-4">
            {loading ? (
              <p className="text-gray-400">Loading...</p>
            ) : bookings.length === 0 ? (
              <p className="text-gray-400">No bookings yet.</p>
            ) : (
              bookings.map((b) => <BookingRow key={b.id} booking={b} onRefresh={loadData} />)
            )}
          </TabsContent>

          <TabsContent value="contacts" className="space-y-4">
            {loading ? (
              <p className="text-gray-400">Loading...</p>
            ) : contacts.length === 0 ? (
              <p className="text-gray-400">No contact submissions yet.</p>
            ) : (
              contacts.map((c) => (
                <div key={c.id} className="glass rounded-xl p-5" data-testid={`contact-row-${c.id}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-heading text-lg font-bold text-white">{c.name}</h3>
                    <Badge className="bg-[#00F0FF]/15 text-[#00F0FF] border-[#00F0FF]/40 capitalize">{c.service_interest}</Badge>
                  </div>
                  <p className="text-gray-400 text-sm mt-1">{c.email}{c.phone ? ` · ${c.phone}` : ''}</p>
                  <p className="text-gray-300 mt-3">{c.message}</p>
                </div>
              ))
            )}
          </TabsContent>
        </Tabs>
      </div>
    </main>
  );
}
